/**
 * Uso do solo (MapBiomas) e CAR no ponto de um foco, publicados pela máquina
 * do agendador (`AmaView-dados/agendador/terreno`).
 *
 * O pixel do MapBiomas tem 30 m, mas a posição de um foco não tem essa
 * precisão (VIIRS 375 m, MODIS 1 km, GOES ~2 km): a máquina conta a cobertura
 * no círculo de 375 m em volta do ponto, ano a ano, de 1985 a 2025. O CAR vem
 * do WFS público do SICAR: os imóveis que tocam o mesmo círculo (o fogo pode
 * estar no vizinho), com o contorno simplificado para o mapa e a marca de quem
 * contém o ponto; só os campos públicos (sem nome nem CPF). Pedido só quando a
 * ficha do foco abre; ~2 KB sem CAR, ~10 KB com os contornos (~3 KB comprimido).
 */
import { fetchWithRetry } from '../lib/fetchRetry';

/** Onde a máquina publica. `VITE_TERRENO_URL` aponta para outro lugar (vazio desliga a seção). */
export const BASE_TERRENO = (import.meta.env.VITE_TERRENO_URL ?? 'https://147.15.84.134/terreno').replace(/\/$/, '');

/** Caixa da Amazônia Legal que a máquina atende (a mesma do terreno.py). */
const CAIXA = { oeste: -74.2, sul: -18.2, leste: -43.8, norte: 5.5 };
/** Metros por grau de latitude (o mesmo do terreno.py). */
export const M_POR_GRAU = 111_320;
const UFS = new Set(['AC', 'AM', 'AP', 'MA', 'MT', 'PA', 'RO', 'RR', 'TO']);

/** Por ano, [[classe, pixels], …], mais pixels primeiro. */
export type ComposicaoAno = [number, number][];

export interface ImovelCar {
  cod: string;
  /** AT ativo · PE pendente · SU suspenso · CA cancelado. */
  status: string | null;
  condicao: string | null;
  areaHa: number | null;
  modulos: number | null;
  /** IRU imóvel rural · AST assentamento · PCT povos e comunidades tradicionais. */
  tipo: string | null;
  municipio: string | null;
  uf: string | null;
  /** O ponto do foco cai dentro dele (os outros só tocam o círculo). */
  contem: boolean;
  /** Contorno simplificado (GeoJSON, lon/lat), para o destaque no mapa. */
  geom: { type: string; coordinates: unknown } | null;
}

export interface Terreno {
  raioM: number;
  mapbiomas: { colecao: string; resolucaoM: number; anos: number[]; comp: ComposicaoAno[] } | null;
  car: { situacao: 'ok' | 'indisponivel' | 'sem_uf'; imoveis: ImovelCar[] };
}

export function dentroDaArea(lat: number, lon: number): boolean {
  return lat >= CAIXA.sul && lat <= CAIXA.norte && lon >= CAIXA.oeste && lon <= CAIXA.leste;
}

/** Endereço do ponto: 4 casas (~11 m), para o cache do servidor acertar. */
export function urlTerreno(lat: number, lon: number, uf: string | null | undefined, base = BASE_TERRENO): string {
  const p = new URLSearchParams({ lat: lat.toFixed(4), lon: lon.toFixed(4) });
  const sigla = uf?.toUpperCase();
  if (sigla && UFS.has(sigla)) p.set('uf', sigla);
  return `${base}/v1?${p}`;
}

/** O mínimo para a ficha não quebrar com uma resposta fora do formato. */
export function validar(d: unknown): Terreno {
  const t = d as Terreno;
  const mb = t?.mapbiomas;
  const okMb = mb === null || (Array.isArray(mb?.anos) && Array.isArray(mb?.comp) && mb.anos.length === mb.comp.length);
  const okCar = Array.isArray(t?.car?.imoveis) && typeof t?.car?.situacao === 'string';
  if (!okMb || !okCar) throw new Error('resposta do terreno fora do formato');
  return t;
}

const cache = new Map<string, Promise<Terreno>>();

/** Uma consulta por ponto na sessão: reabrir a ficha não pede de novo. */
export function buscarTerreno(lat: number, lon: number, uf: string | null | undefined): Promise<Terreno> {
  const url = urlTerreno(lat, lon, uf);
  let p = cache.get(url);
  if (!p) {
    p = fetchWithRetry(url, {}, { retries: 1, timeoutMs: 20_000, read: async (r) => validar(await r.json()) });
    // Falha, ou o SICAR fora do ar naquela hora: a próxima abertura da ficha pede de novo.
    p.then(
      (t) => t.car.situacao === 'indisponivel' && cache.delete(url),
      () => cache.delete(url),
    );
    cache.set(url, p);
  }
  return p;
}
